/**
 * FirstTimeMatchWizard — soft, dismissible modal that fires at session
 * start when:
 *   (a) this is the user's first session with the match, OR
 *   (b) the latest digest has no open_threads.
 *
 * Phases:
 *   write      → intro copy + hint list + textarea (user can Skip or
 *                Structure My Notes to proceed)
 *   extracting → spinner while perspective_seeder LLM runs
 *   preview    → tick/untick claims grouped by domain
 *   greeting   → spinner while Eros greets via wizard_intro_greeting LLM
 *
 * On close (commit OR skip) parent receives a callback to refresh the chat
 * history so the new assistant greeting message appears.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Loader2, Sparkles, CheckCircle2, X, PencilLine, Check } from "lucide-react";
import { authAxios } from "../../utils/auth";

const HINTS = [
  "How did you meet, and where are things right now?",
  "What's drawing you to them?",
  "Anything you've recently noticed — words, gestures, friction?",
  "Anything that's been on your mind that you haven't said out loud?",
];

export default function FirstTimeMatchWizard({
  open,
  onClose,
  matchId,
  matchName,
  userPersonaName,
  sessionId,
  getSessionId,
}) {
  // BUG FIX — belt-and-braces:
  // `sessionId` is captured at the moment the wizard opens. If the parent
  // changes sessions before submit (e.g. the user clicks a different
  // session in the list while the wizard is open), the static prop is
  // stale and the wizard-greet POST 404s. Prefer the live getter when
  // provided.
  const resolveSessionId = () =>
    (typeof getSessionId === "function" ? getSessionId() : null) || sessionId;
  const [phase, setPhase] = useState("write"); // write|extracting|preview|greeting|done
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [edits, setEdits] = useState({}); // { [idx]: { claim_text: "..." } }
  const [editingIdx, setEditingIdx] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setPhase("write");
      setText("");
      setPreview(null);
      setSelected(new Set());
      setEdits({});
      setEditingIdx(null);
      setEditDraft("");
      setError(null);
    }
  }, [open]);

  const grouped = useMemo(() => {
    if (!preview?.claims) return {};
    const g = {};
    preview.claims.forEach((c, idx) => {
      if (!g[c.domain]) g[c.domain] = [];
      g[c.domain].push({ ...c, _idx: idx });
    });
    return g;
  }, [preview]);

  function toggle(idx) {
    const next = new Set(selected);
    if (next.has(idx)) next.delete(idx);
    else next.add(idx);
    setSelected(next);
  }

  function startEdit(idx) {
    const claim = preview?.claims?.[idx];
    if (!claim) return;
    setEditingIdx(idx);
    setEditDraft(edits[idx]?.claim_text ?? claim.claim_text);
  }

  function cancelEdit() {
    setEditingIdx(null);
    setEditDraft("");
  }

  function saveEdit() {
    if (editingIdx == null) return;
    const trimmed = editDraft.trim();
    const original = preview.claims[editingIdx].claim_text.trim();
    setEdits((prev) => {
      const next = { ...prev };
      if (!trimmed || trimmed === original) {
        delete next[editingIdx];
      } else {
        next[editingIdx] = { claim_text: trimmed };
      }
      return next;
    });
    setEditingIdx(null);
    setEditDraft("");
  }

  async function extract() {
    setError(null);
    setPhase("extracting");
    try {
      const res = await authAxios.post(
        `/relationship/${matchId}/perspective/seed-from-freetext`,
        { free_text: text, coach_session_id: sessionId || null }
      );
      setPreview(res.data);
      // Pre-select all claims
      setSelected(new Set(res.data.claims.map((_, i) => i)));
      setPhase("preview");
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      let msg;
      if (status === 401) {
        msg = "Your session expired. Please refresh the page and sign in again.";
      } else if (status === 503) {
        msg = detail || "Eros's language model is briefly unavailable. Try again in 30 seconds.";
      } else if (status === 400) {
        msg = detail || "Couldn't read those notes. Try a longer or clearer description.";
      } else {
        msg = "Extraction failed. Try again?";
      }
      setError(msg);
      setPhase("write");
    }
  }

  async function commitAndGreet() {
    setError(null);
    setPhase("greeting");
    try {
      // Commit selected claims first (claims land active)
      if (selected.size > 0 && preview) {
        await authAxios.post(
          `/relationship/${matchId}/perspective/seed-from-freetext/commit`,
          {
            preview_id: preview.preview_id,
            accepted_indices: [...selected],
            edits: Object.keys(edits).length > 0 ? edits : null,
          }
        );
      }
      // Then have Eros greet
      await authAxios.post(`/relationship/${matchId}/wizard-greet`, {
        session_id: resolveSessionId(),
        mode: "seeded",
      });
      finish();
    } catch (e) {
      setError(
        e.response?.data?.detail ||
          "Couldn't finalise the wizard. You can still chat — Eros will catch up."
      );
      setPhase("preview");
    }
  }

  async function skip() {
    setError(null);
    setPhase("greeting");
    try {
      await authAxios.post(`/relationship/${matchId}/wizard-greet`, {
        session_id: resolveSessionId(),
        mode: "skipped",
      });
    } catch (e) {
      // Non-fatal — close anyway
      console.warn("Skip greeting failed:", e);
    }
    finish();
  }

  function finish() {
    setPhase("done");
    setTimeout(() => onClose && onClose({ committed: true }), 200);
  }

  if (!open) return null;

  // Defensive: don't allow the Dialog to close itself while an LLM call is in
  // flight (otherwise a focus blip would kill the wizard mid-extract / mid-greet).
  const isInFlight = phase === "extracting" || phase === "greeting";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) return;
        if (isInFlight) return;
        onClose && onClose({ committed: false });
      }}
    >
      <DialogContent
        className="bg-divine-charcoal border-divine-gold/25 text-white/85 max-w-xl max-h-[85vh] overflow-y-auto"
        data-testid="first-time-wizard"
      >
        <DialogHeader>
          <DialogTitle className="font-cinzel text-divine-gold flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Quick setup for {matchName}
          </DialogTitle>
        </DialogHeader>

        {/* WRITE — merged intro + notes entry */}
        {phase === "write" && (
          <div className="space-y-3">
            <p className="text-sm leading-relaxed text-white/85">
              Do you want Agent Eros to start this session with new insights
              about <span className="text-divine-gold">{matchName}</span>?
              Spend two minutes telling us what you already know and we'll
              structure it so your session has solid grounding.
            </p>
            <p className="text-xs text-white/55 uppercase tracking-wide pt-1">
              Some things to think about
            </p>
            <ul
              className="text-xs text-white/65 space-y-1 list-none pl-0"
              data-testid="wizard-hints"
            >
              {HINTS.map((h) => (
                <li key={h} className="flex gap-2">
                  <span className="text-divine-gold/60">•</span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={`Tell Eros what you know about ${matchName}…`}
              className="w-full h-44 bg-black/50 border border-divine-gold/20 rounded-md p-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-divine-gold/50 resize-none"
              data-testid="wizard-textarea"
            />
            <div className="flex items-center justify-between pt-1">
              <button
                onClick={skip}
                className="text-white/45 hover:text-white/75 text-xs transition-colors"
                data-testid="wizard-skip-from-write-btn"
              >
                Skip for now
              </button>
              <button
                onClick={extract}
                disabled={text.trim().length < 20}
                className="bg-divine-gold text-divine-black hover:bg-divine-gold/90 text-sm px-4 py-2 rounded-md font-medium disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="wizard-extract-btn"
              >
                Structure my notes
              </button>
            </div>
          </div>
        )}

        {/* EXTRACTING */}
        {phase === "extracting" && (
          <div className="flex items-center gap-3 text-white/65 text-sm py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Reading your notes about {matchName}…
          </div>
        )}

        {/* PREVIEW */}
        {phase === "preview" && preview && (
          <div className="space-y-3">
            {preview.claims.length === 0 ? (
              <div className="text-sm text-amber-300/90 bg-amber-500/5 border border-amber-500/20 rounded-md p-3 space-y-2">
                <div className="font-medium">Eros couldn't extract any claims from this text.</div>
                <div className="text-xs text-white/70">
                  This usually means the notes were too short, too abstract, or
                  didn't reference {matchName} clearly. Try writing more about
                  communication, trust, how you're feeling about them, or
                  specific things they've said or done.
                </div>
                {preview.truncated && (
                  <div className="text-xs text-rose-300 italic">
                    (The response was truncated — try shorter, more focused notes.)
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => setPhase("write")}
                    className="bg-divine-gold/20 hover:bg-divine-gold/30 text-divine-gold text-xs px-3 py-1.5 rounded transition-colors"
                  >
                    Edit notes & try again
                  </button>
                  <button
                    onClick={skip}
                    className="text-white/45 hover:text-white/75 text-xs px-2 transition-colors"
                  >
                    Skip
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-xs text-white/55">
                  Found <span className="text-divine-gold">{preview.claims.length}</span>{" "}
                  claim{preview.claims.length === 1 ? "" : "s"}. Untick anything that
                  doesn't ring true. You can edit, lock, or retract any of them later.
                  {preview.truncated && (
                    <span className="text-amber-300/90 ml-2 italic">
                      (Output was partially truncated — these are the complete claims that came through.)
                    </span>
                  )}
                </p>
                <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2">
                  {Object.entries(grouped).map(([domain, claims]) => (
                    <div key={domain}>
                      <div className="text-[10px] uppercase tracking-wider text-divine-gold/70 mb-1">
                        {domain.replace(/_/g, " ")}
                      </div>
                      {claims.map((c) => {
                        const isEditing = editingIdx === c._idx;
                        const editedText = edits[c._idx]?.claim_text;
                        const displayText = editedText ?? c.claim_text;
                        return (
                          <div
                            key={c._idx}
                            className="flex gap-2 items-start py-1.5 px-2 rounded hover:bg-divine-gold/5 group"
                          >
                            <input
                              type="checkbox"
                              checked={selected.has(c._idx)}
                              onChange={() => toggle(c._idx)}
                              className="mt-1.5 accent-divine-gold cursor-pointer"
                              disabled={isEditing}
                            />
                            <div className="text-sm flex-1 min-w-0">
                              {isEditing ? (
                                <div className="space-y-1.5">
                                  <textarea
                                    value={editDraft}
                                    onChange={(e) => setEditDraft(e.target.value)}
                                    className="w-full bg-black/50 border border-divine-gold/40 rounded p-2 text-sm text-white resize-none focus:outline-none focus:border-divine-gold"
                                    rows={3}
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === "Escape") cancelEdit();
                                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveEdit();
                                    }}
                                  />
                                  <div className="flex gap-2 text-xs">
                                    <button
                                      onClick={saveEdit}
                                      className="bg-divine-gold/30 hover:bg-divine-gold/50 text-divine-gold px-2 py-0.5 rounded flex items-center gap-1"
                                      data-testid={`wizard-save-edit-${c._idx}`}
                                    >
                                      <Check className="h-3 w-3" />
                                      Save
                                    </button>
                                    <button
                                      onClick={cancelEdit}
                                      className="text-white/50 hover:text-white/80 px-2"
                                    >
                                      Cancel
                                    </button>
                                    <span className="text-white/30 self-center text-[10px] ml-auto">
                                      ⌘↵ to save · esc to cancel
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-start gap-2">
                                    <div
                                      className="text-white/85 flex-1 cursor-pointer"
                                      onClick={() => toggle(c._idx)}
                                    >
                                      {displayText}
                                      {editedText != null && (
                                        <span className="ml-2 text-[10px] text-divine-gold/70 italic">
                                          (edited)
                                        </span>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => startEdit(c._idx)}
                                      className="text-white/30 hover:text-divine-gold transition-colors opacity-0 group-hover:opacity-100 shrink-0"
                                      title="Edit claim text"
                                      data-testid={`wizard-edit-${c._idx}`}
                                    >
                                      <PencilLine className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                  {c.evidence_summary && (
                                    <div className="text-[11px] text-white/45 italic mt-0.5">
                                      {c.evidence_summary}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-2">
                  <button
                    onClick={skip}
                    className="text-white/45 hover:text-white/75 text-xs transition-colors"
                  >
                    Skip
                  </button>
                  <button
                    onClick={commitAndGreet}
                    disabled={selected.size === 0}
                    className="bg-divine-gold text-divine-black hover:bg-divine-gold/90 text-sm px-4 py-2 rounded-md font-medium disabled:opacity-30 transition-colors flex items-center gap-2"
                    data-testid="wizard-commit-btn"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Add {selected.size} & let Eros begin
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* GREETING */}
        {phase === "greeting" && (
          <div className="flex items-center gap-3 text-white/65 text-sm py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            {userPersonaName ? `Eros is preparing to greet you, ${userPersonaName}…` : "Eros is preparing to greet you…"}
          </div>
        )}

        {error && (
          <div className="mt-3 text-xs text-rose-300 flex items-start gap-2 bg-rose-500/5 border border-rose-500/20 rounded p-2">
            <X className="h-3 w-3 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
